import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import PropTypes from "prop-types";

import LoggedInLayout from "../../layout/LoggedInLayout";
import { useToast } from "../../hook/useToast";
import {
  bookSkillProviderAppointment,
  fetchSkillProviderAvailability,
  fetchSkillProviders,
} from "../../services/api";
import CouncilBookingDialog from "../Council/CouncilBookingDialog";
import CouncilBookingSuccessDialog from "../Council/CouncilBookingSuccessDialog";
import SkillProviderCard from "./SkillProviderCard";
import SkillProviderDetailDialog from "./SkillProviderDetailDialog";

const PAGE_SIZE = 8;
const NAVY = "#292E5C";
const ORANGE = "#F7AA47";
const BOOKING_TIMEZONE = "Asia/Kolkata";

const getSkillProviderId = (provider) =>
  provider?.id || provider?.skill_provider_id || null;

const getTodayISODate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString().slice(0, 10);
};

const searchInputSx = {
  flex: 1,
  "& .MuiOutlinedInput-root": {
    height: 52,
    borderRadius: "8px 0 0 8px",
    bgcolor: ORANGE,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
    fontSize: 15,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    "&::placeholder": {
      color: "#FFFEFF",
      opacity: 1,
    },
  },
};

const SkillProviders = ({ studentUserId, studentName, childSelector }) => {
  const { showToast } = useToast();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedSkill, setSelectedSkill] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [availableFilters, setAvailableFilters] = useState({
    skills: [],
    cities: [],
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [bookingProvider, setBookingProvider] = useState(null);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);
  const [bookingAvailabilityDays, setBookingAvailabilityDays] = useState([]);
  const [bookingAvailabilityLoading, setBookingAvailabilityLoading] = useState(false);
  const [bookingAvailabilityError, setBookingAvailabilityError] = useState("");
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  useEffect(() => {
    setSelectedProvider(null);
    setBookingProvider(null);
    setBookingConfirmation(null);
    setBookingAvailabilityDays([]);
    setBookingAvailabilityError("");
  }, [studentUserId]);

  // Debounce search term
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setCurrentPage(1);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  // Load skill providers from API
  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetchSkillProviders({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearchTerm,
        skill: selectedSkill,
        city: selectedCity,
      });
      const payload = response.data?.data || {};

      setProviders(
        Array.isArray(payload.skill_providers) ? payload.skill_providers : []
      );
      setTotalPages(payload.pagination?.total_pages || 0);
      setAvailableFilters({
        skills: Array.isArray(payload.filters?.skills)
          ? payload.filters.skills
          : [],
        cities: Array.isArray(payload.filters?.cities)
          ? payload.filters.cities
          : [],
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to load skill providers right now.";

      setError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, selectedCity, selectedSkill, showToast]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setDebouncedSearchTerm(searchTerm.trim());
    setCurrentPage(1);
  };

  const handleSelectProvider = (provider) => {
    setBookingProvider(provider);
  };

  const handleViewProviderDetail = (provider) => {
    setSelectedProvider(provider);
  };

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setIsFilterOpen(false);
    loadProviders();
  };

  const handleResetFilter = () => {
    setSelectedSkill("");
    setSelectedCity("");
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const loadBookingAvailability = useCallback(async (provider) => {
    const skillProviderId = getSkillProviderId(provider);

    if (!skillProviderId) {
      setBookingAvailabilityDays([]);
      setBookingAvailabilityError("Skill provider details are missing.");
      return;
    }

    setBookingAvailabilityLoading(true);
    setBookingAvailabilityError("");

    try {
      const response = await fetchSkillProviderAvailability({
        skillProviderId,
        startDate: getTodayISODate(),
        days: 7,
        timezone: BOOKING_TIMEZONE,
      });
      setBookingAvailabilityDays(response.data?.data?.days || []);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to load available slots right now.";

      setBookingAvailabilityDays([]);
      setBookingAvailabilityError(message);
    } finally {
      setBookingAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bookingProvider) {
      loadBookingAvailability(bookingProvider);
    } else {
      setBookingAvailabilityDays([]);
      setBookingAvailabilityError("");
      setBookingAvailabilityLoading(false);
      setBookingSubmitting(false);
    }
  }, [bookingProvider, loadBookingAvailability]);

  const handleBookSlot = async (booking) => {
    const skillProviderId = getSkillProviderId(booking.counsellor);

    if (!skillProviderId || !booking.startAt) {
      setBookingProvider(null);
      setBookingConfirmation(booking);
      return;
    }

    setBookingSubmitting(true);

    try {
      const response = await bookSkillProviderAppointment({
        skillProviderId,
        startAt: booking.startAt,
        timezone: BOOKING_TIMEZONE,
        studentUserId,
      });
      setBookingProvider(null);
      setBookingConfirmation({
        ...booking,
        studentName,
        appointment: response.data?.data || null,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to book this slot right now.";

      showToast(message, "error");
      await loadBookingAvailability(booking.counsellor);
    } finally {
      setBookingSubmitting(false);
    }
  };

  return (
    <LoggedInLayout searchBar={false}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: "#fff",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, md: 5 },
        }}
      >
        {childSelector}
        {/* Search Bar */}
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            width: "100%",
            display: "flex",
            mb: { xs: 3, md: 4 },
          }}
        >
          <TextField
            fullWidth
            value={searchTerm}
            placeholder="Search"
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={searchInputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#FFFEFF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            sx={{
              width: { xs: 92, sm: 120 },
              height: 52,
              borderRadius: "0 8px 8px 0",
              bgcolor: NAVY,
              color: "#FFFEFF",
              fontSize: 15,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#20264F",
              },
            }}
          >
            Search
          </Button>
        </Box>

        {/* Title and Filter Button */}
        <Box
          sx={{
            width: "100%",
            position: "relative",
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              minHeight: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              backgroundColor:"#f3f3f5",
              px:2,
              borderRadius:3
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <SchoolIcon sx={{ width: 28, height: 28, color: NAVY }} />
              <Typography
                sx={{
                  color: "#231F20",
                  fontSize: 15,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                }}
              >
                Skill Providers
              </Typography>
            </Box>

            <Button
              type="button"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              onClick={() => setIsFilterOpen((value) => !value)}
              sx={{
                height: 34,
                px: 1.25,
                py: 0.875,
                bgcolor: "#fff",
                borderColor: "#E2ECF3",
                borderRadius: "7px",
                color: "#363636",
                fontSize: 12,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                lineHeight: "15px",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#fff",
                  borderColor: "#D6DFE8",
                },
              }}
            >
              Filter
            </Button>
          </Box>

          {/* Filter Panel */}
          {isFilterOpen ? (
            <Paper
              elevation={0}
              sx={{
                position: { xs: "static", md: "absolute" },
                right: 0,
                top: 46,
                zIndex: 4,
                width: { xs: "100%", sm: 221 },
                mt: { xs: 1.5, md: 0 },
                p: 2,
                bgcolor: "#F7F7F7",
                borderRadius: "8px",
                boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color: "#111827",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  Filter Now
                </Typography>
                <Button
                  type="button"
                  aria-label="Close filters"
                  onClick={() => setIsFilterOpen(false)}
                  sx={{
                    minWidth: 0,
                    width: 24,
                    height: 24,
                    p: 0,
                    color: "#6B7280",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 ,gap:1.5}}>
                <Select
                  value={selectedCity}
                  displayEmpty
                  onChange={(event) => setSelectedCity(event.target.value)}
                  sx={{
                    height: 36,
                    borderRadius: "7px",
                    bgcolor: "#fff",
                    color: selectedCity ? "#000" : "#888888",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#DCDFDE",
                    },
                  }}
                >
                  <MenuItem value="">-- Select City --</MenuItem>
                  {availableFilters.cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
                
                <Select
                  value={selectedSkill}
                  displayEmpty
                  onChange={(e) => setSelectedSkill(e.target.value)}
                  sx={{
                    height: 36,
                    borderRadius: "7px",
                    bgcolor: "#fff",
                    color: selectedSkill ? "#000" : "#888888",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#DCDFDE",
                    },
                  }}
                >
                  <MenuItem value="">-- Select Skills --</MenuItem>
                  {availableFilters.skills.map((skill) => (
                    <MenuItem key={skill} value={skill}>
                      {skill}
                    </MenuItem>
                  ))}
                </Select>
                
              </FormControl>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  type="button"
                  onClick={handleResetFilter}
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#374151",
                    fontSize: 12,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    lineHeight: "14px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#fff",
                      borderColor: "#D1D5DB",
                    },
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  onClick={handleApplyFilter}
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#374151",
                    fontSize: 12,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    lineHeight: "14px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#fff",
                      borderColor: "#D1D5DB",
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Paper>
          ) : null}
        </Box>

        {/* Description */}
        <Typography
          sx={{
            color: "#231F20",
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            lineHeight: "31.5px",
            mb: 3,
          }}
        >
          Here is the list of the best Skill Providers.
          <br />
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "31.5px",
            }}
          >
            Select your skill provider to build or upgrade your skills.
          </Box>
        </Typography>

        {/* Error Alert */}
        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadProviders}>
                Retry
              </Button>
            }
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        ) : null}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: NAVY }} />
          </Box>
        ) : null}

        {/* Content */}
        {!loading && !error ? (
          <>
            {/* Grid of Cards */}
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: { xs: "12px", md: "20px" },
              }}
            >
              {providers.map((provider) => (
                <SkillProviderCard
                  key={provider.id || provider.skill_provider_id}
                  provider={provider}
                  onDetail={handleViewProviderDetail}
                  onSelect={handleSelectProvider}
                />
              ))}
            </Box>

            {/* Empty State */}
            {providers.length === 0 ? (
              <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 7 }}>
                <Typography
                  sx={{
                    color: "#231F20",
                    fontSize: 15,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {debouncedSearchTerm || selectedSkill
                    || selectedCity
                    ? `No skill providers found matching your criteria.`
                    : "No skill providers available right now."}
                </Typography>
              </Box>
            ) : null}

            {/* Pagination */}
            {totalPages > 1 ? (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: 2,
                  mt: 3,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      width: 32,
                      height: 32,
                      borderRadius: "4px",
                      border: "0.55px solid rgba(128, 128, 128, 0.50)",
                      color: "#000",
                      fontSize: 14,
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 500,
                      lineHeight: "20px",
                      backgroundColor:"#fff"
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      bgcolor: "#fff",
                      border: `1px solid #F7AA47`,
                      color: "#F7AA47",
                    },
                    "& .MuiPaginationItem-root.Mui-selected:hover": {
                      bgcolor: "#fff",
                    },
                  }}
                />
                
              </Box>
            ) : null}
          </>
        ) : null}

        <SkillProviderDetailDialog
          open={Boolean(selectedProvider)}
          provider={selectedProvider}
          onClose={() => setSelectedProvider(null)}
        />
        <CouncilBookingDialog
          open={Boolean(bookingProvider)}
          counsellor={bookingProvider}
          availabilityDays={bookingAvailabilityDays}
          loading={bookingAvailabilityLoading}
          error={bookingAvailabilityError}
          bookingLoading={bookingSubmitting}
          onClose={() => setBookingProvider(null)}
          onBookSlot={handleBookSlot}
          onRetry={() => loadBookingAvailability(bookingProvider)}
        />
        <CouncilBookingSuccessDialog
          open={Boolean(bookingConfirmation)}
          booking={bookingConfirmation}
          onClose={() => setBookingConfirmation(null)}
        />
      </Box>
    </LoggedInLayout>
  );
};

SkillProviders.propTypes = {
  studentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentName: PropTypes.string,
  childSelector: PropTypes.node,
};

export default SkillProviders;
